import { useState } from 'react';

import { browserHistory } from '@/routing/browserHistory';
import { getFullWorkspaceLink } from '@/utils/getLinkWithSubscriptionId.utils';
import ContractNumbersModal from '../../Customers/components/ContractNumbersModal/ContractNumbersModal';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { StyledLink } from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import basicCellStyles from '../../Customers/components/cellStyles.module.scss';

interface ContractNumberRendererProps {
  data: {
    contracts?: Array<{ id: string; contractNumber?: string | null } | null> | null;
    contractNumber?: string | null;
    salesOrderID?: string | null;
  };
  getContractLink: (contractId: string, searchString?: string) => string;
  searchString?: string;
}

const MAX_CHARS_LENGTH_IN_MODAL = 70;

export const ContractNumberRenderer = ({ data, getContractLink, searchString }: ContractNumberRendererProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const contracts = (data?.contracts ?? []).filter(Boolean);

  if (!contracts.length) {
    return null;
  }

  if (contracts.length === 1) {
    const contractId = contracts[0]?.id ?? '';
    const internalHref = contractId ? getContractLink(contractId, searchString) : '#';
    const href = internalHref === '#' ? '#' : getFullWorkspaceLink(internalHref);

    return (
      <StyledLink
        href={href}
        onClick={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.button === 0) {
            e.stopPropagation();
            return;
          }
          if (e.button === 0) {
            e.preventDefault();
          }
        }}
      >
        {data?.contractNumber}
      </StyledLink>
    );
  }

  const handleContractLinkClick = (e: React.MouseEvent, contractId: string) => {
    if ((e.ctrlKey || e.metaKey) && e.button === 0) {
      e.stopPropagation();
      return;
    }
    if (e.button === 0) {
      e.preventDefault();
      browserHistory.push(getContractLink(contractId, searchString));
      setIsModalOpen(false);
    }
  };

  return (
    <>
      {isModalOpen && (
        <ContractNumbersModal
          isOpen={isModalOpen}
          title={`Contracts for Consolidated Sales Order ${data?.salesOrderID ?? ''}`}
          onClose={() => setIsModalOpen(false)}
        >
          <div className={basicCellStyles['modal-container']}>
            {contracts.map((contract) => (
              <a
                key={contract?.id}
                href={getFullWorkspaceLink(getContractLink(contract?.id ?? '', searchString))}
                title={FormattingUtils.getTitleIfValueIsTrimmed(
                  contract?.contractNumber ?? '',
                  MAX_CHARS_LENGTH_IN_MODAL,
                )}
                onClick={(e) => handleContractLinkClick(e, contract?.id ?? '')}
                className={basicCellStyles['modal-link']}
              >
                {FormattingUtils.getValueTrimmed(
                  contract?.contractNumber ?? '',
                  MAX_CHARS_LENGTH_IN_MODAL,
                )}
              </a>
            ))}
          </div>
        </ContractNumbersModal>
      )}
      <div
        aria-hidden
        onClick={() => setIsModalOpen(true)}
        className={basicCellStyles['cell-style']}
      >
        {contracts.length} contracts
      </div>
    </>
  );
};
